import React, { useEffect, useMemo, useState } from "react";
import { X, BarChart3, ChevronDown, TrendingUp, LineChart as LineIcon } from "lucide-react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip as RTooltip, ResponsiveContainer, ReferenceLine,
} from "recharts";
import { apiClient, fetchIndicators } from "../lib/api";
import { formatNumber } from "../lib/format";

const TIMEFRAMES = ["1M", "3M", "6M", "1Y", "5Y", "MAX"];

export const MainIndicatorsModal = ({ open, onClose }) => {
  const [data, setData] = useState(null);
  const [openCat, setOpenCat] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [timeframe, setTimeframe] = useState("5Y");
  const [series, setSeries] = useState(null);
  const [seriesLoading, setSeriesLoading] = useState(false);

  useEffect(() => {
    if (!open) return;
    fetchIndicators()
      .then((d) => {
        if (d?.success) {
          setData(d);
          const expanded = {};
          (d.uiCategoryOrder || []).forEach((c, i) => { expanded[c] = i < 3; });
          setOpenCat(expanded);
          const first = d.groupsOrdered?.[0]?.items?.[0];
          if (first) setSelectedItem(first);
        }
      })
      .catch((err) => console.warn("Indicators fetch failed:", err));
  }, [open]);

  useEffect(() => {
    if (!selectedItem || !open) return;
    setSeriesLoading(true);
    apiClient
      .get(`/proxy/get_indicator_detail/${selectedItem.id}?timeframe=${timeframe}`)
      .then((r) => {
        const d = r.data;
        const pts = (d?.history || []).map((p) => ({
          date: p.date,
          t: p.t,
          value: typeof p.value === "number" ? p.value : 0,
          mom: p.mom,
        }));
        setSeries(pts);
      })
      .catch(() => setSeries([]))
      .finally(() => setSeriesLoading(false));
  }, [selectedItem, timeframe, open]);

  const latest = series && series.length ? series[series.length - 1] : null;
  const prev = series && series.length > 1 ? series[series.length - 2] : null;
  const change = latest && prev ? latest.value - prev.value : 0;
  const changePct = prev && prev.value ? (change / prev.value) * 100 : 0;
  const high = useMemo(() => series?.reduce((m, p) => Math.max(m, p.value), -Infinity) ?? 0, [series]);
  const low = useMemo(() => series?.reduce((m, p) => Math.min(m, p.value), Infinity) ?? 0, [series]);

  if (!open) return null;
  const groups = data?.groupsOrdered || [];

  return (
    <div className="nms-modal-backdrop full-modal" data-testid="indicators-modal" onClick={onClose}>
      <div className="nms-modal indicators-modal" onClick={(e) => e.stopPropagation()}>
        <button className="nms-modal-close" onClick={onClose} aria-label="Close" data-testid="indicators-close">
          <X size={18} />
        </button>

        <header className="ind-head">
          <div className="ind-brand-square"><LineIcon size={20} /></div>
          <div>
            <h2 className="ind-title">NMS Data Explorer</h2>
            <p className="ind-sub">Indikator Ekonomi Indonesia</p>
          </div>
        </header>

        <div className="ind-body">
          <aside className="ind-sidebar">
            {groups.map((g) => {
              const opened = !!openCat[g.uiCategory];
              return (
                <div key={g.uiCategory} className={`ind-cat ${opened ? "open" : ""}`}>
                  <button
                    type="button"
                    className="ind-cat-head"
                    onClick={() => setOpenCat((s) => ({ ...s, [g.uiCategory]: !opened }))}
                    data-testid={`ind-cat-${g.uiCategory.replace(/\s+/g, "_")}`}
                  >
                    <BarChart3 size={14} />
                    <span className="ic-name">{g.uiCategory}</span>
                    <span className="ic-count">{g.items.length}</span>
                    <ChevronDown size={14} className={`ic-chev ${opened ? "rot" : ""}`} />
                  </button>
                  {opened && (
                    <ul className="ind-items">
                      {g.items.map((it) => {
                        const active = selectedItem?.id === it.id;
                        return (
                          <li key={it.id}>
                            <button
                              type="button"
                              className={`ind-item ${active ? "active" : ""}`}
                              onClick={() => setSelectedItem(it)}
                              data-testid={`ind-item-${it.id}`}
                            >
                              <span className="ii-name">{it.shortName || it.name}</span>
                              <span className="ii-freq">{(it.nativeFrequency || "").toUpperCase()}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </div>
              );
            })}
          </aside>

          <section className="ind-main">
            {selectedItem && (
              <>
                <div className="ind-detail-head">
                  <div className="ind-icon-box"><TrendingUp size={20} /></div>
                  <div>
                    <h3 className="ind-detail-title">{selectedItem.name}</h3>
                    <div className="ind-tags">
                      <span className="ind-tag">{selectedItem.source}</span>
                      <span className="ind-tag green">{selectedItem.nativeFrequency?.toUpperCase()}</span>
                      <span className="ind-tag purple">KUARTALAN</span>
                    </div>
                  </div>
                </div>

                <div className="ind-info-grid">
                  <div className="ind-info-card">
                    <div className="iic-lbl">
                      Terakhir{latest?.date ? ` (${new Date(latest.date).toLocaleDateString("id-ID", { day: "2-digit", month: "long", year: "numeric" })})` : ""}
                    </div>
                    <div className="iic-big-val">
                      {latest ? formatNumber(latest.value, selectedItem.decimals ?? 2) : "—"}
                      <span className="iic-unit">{selectedItem.unit}</span>
                    </div>
                    <div className={`iic-change ${change > 0 ? "up" : change < 0 ? "down" : "flat"}`}>
                      {change > 0 ? "↑" : change < 0 ? "↓" : "•"} {formatNumber(Math.abs(change), 2)} ({changePct > 0 ? "+" : ""}{changePct.toFixed(2)}%)
                    </div>
                  </div>
                  <div className="ind-info-card">
                    <div className="iic-lbl">Range ({timeframe})</div>
                    <div className="iic-range">
                      <div className="iic-range-bar"><div className="iic-range-thumb" /></div>
                      <div className="iic-range-vals">
                        <span><em>Low</em> <strong className="r-low">{formatNumber(low, 2)}</strong></span>
                        <span><em>High</em> <strong className="r-high">{formatNumber(high, 2)}</strong></span>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="ind-tf-row">
                  <span className="tf-lbl">TIMEFRAME</span>
                  {TIMEFRAMES.map((tf) => (
                    <button
                      key={tf}
                      type="button"
                      className={`tf-btn ${timeframe === tf ? "active" : ""}`}
                      onClick={() => setTimeframe(tf)}
                      data-testid={`tf-${tf}`}
                    >
                      {tf}
                    </button>
                  ))}
                </div>

                <div className="ind-chart">
                  {seriesLoading ? (
                    <div className="anal-loading">Memuat data…</div>
                  ) : series && series.length ? (
                    <ResponsiveContainer width="100%" height={280}>
                      <AreaChart data={series}>
                        <defs>
                          <linearGradient id="ind-fill" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor="#10b981" stopOpacity={0.35} />
                            <stop offset="100%" stopColor="#10b981" stopOpacity={0.02} />
                          </linearGradient>
                        </defs>
                        <XAxis dataKey="date" stroke="#475569" tick={{ fontSize: 9, fill: "#94a3b8" }}
                          tickFormatter={(v) => v ? new Date(v).toLocaleDateString("id-ID", { month: "short", year: "2-digit" }) : ""} />
                        <YAxis stroke="#475569" tick={{ fontSize: 10, fill: "#94a3b8" }} />
                        <RTooltip contentStyle={{ background: "#0a1628", border: "1px solid #10b981", borderRadius: 8, fontSize: 11 }} />
                        <Area type="monotone" dataKey="value" stroke="#10b981" strokeWidth={2} fill="url(#ind-fill)" />
                        {latest && <ReferenceLine y={latest.value} stroke="#10b981" strokeDasharray="3 3" />}
                      </AreaChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="anal-loading">Tidak ada data untuk timeframe ini.</div>
                  )}
                </div>
              </>
            )}
          </section>
        </div>
      </div>
    </div>
  );
};

export default MainIndicatorsModal;
